import EmailDomainInput from '@/components/features/Employees/EmailDomainInput';
import InputError from '@/components/layout/InputError';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select';
import { useTranslations } from '@/hooks/useTranslations';
import CreateLayout from '@/layouts/create-layout';
import { type BreadcrumbItem } from '@/types';
import { Head, Link, useForm, usePage } from '@inertiajs/react';
import { BriefcaseBusiness, Info, LoaderCircle, Lock, Mail, Phone, User } from 'lucide-react';
import { FormEventHandler } from 'react';

type CreateEmployeeForm = {
    first_name: string;
    last_name: string;
    email: string;
    password: string;
    password_confirmation: string;
    phone?: string;
    role?: string;
};

interface CreateEmployeePageProps {
    approvedEmailDomains?: string[];
    [key: string]: unknown;
}

export default function CreateEmployee() {
    const { t } = useTranslations();
    const { props } = usePage<CreateEmployeePageProps>();
    const approvedDomains = props.approvedEmailDomains || [];
    const requiredMark = <span className="text-destructive ml-1">*</span>;

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: t('employees.create.breadcrumbs.dashboard', 'Dashboard'),
            href: '/dashboard',
        },
        {
            title: t('employees.create.breadcrumbs.employees', 'Employees'),
            href: '/employees',
        },
        {
            title: t('employees.create.breadcrumbs.create', 'Create'),
            href: '/employees/create',
        },
    ];

    const { data, setData, post, processing, errors, reset } = useForm<Required<CreateEmployeeForm>>({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        password_confirmation: '',
        phone: '',
        role: '',
    });

    const submit: FormEventHandler = (e) => {
        e.preventDefault();
        post(route('employees.store'), {
            onFinish: () => reset('password', 'password_confirmation'),
        });
    };

    return (
        <CreateLayout
            breadcrumbs={breadcrumbs}
            title={t('employees.create.heading', 'Create Employee')}
            subtitle={t('employees.create.subheading', 'Add a new employee to your system')}
        >
            <Head title={t('employees.create.title', 'Create Employee')} />

            <form onSubmit={submit} className="space-y-6 pb-6">
                <Card className="rounded-md">
                    <CardHeader className="pb-4">
                        <div className="flex items-start gap-3">
                            <span className="bg-primary/12 text-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                                1
                            </span>
                            <div className="space-y-1">
                                <CardTitle>{t('employees.create.sections.main_info', 'Main information')}</CardTitle>
                                <CardDescription>{t('employees.create.sections.main_info_desc', 'Main employee details.')}</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-5">
                        <div className="grid gap-5 md:grid-cols-2">
                            <div className="space-y-2">
                                <Label htmlFor="first_name">
                                    {t('employees.create.labels.first_name', 'First name')}
                                    {requiredMark}
                                </Label>
                                <div className="relative">
                                    <User className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                                    <Input
                                        id="first_name"
                                        className="pl-10"
                                        required
                                        autoFocus
                                        autoComplete="given-name"
                                        value={data.first_name}
                                        onChange={(e) => setData('first_name', e.target.value)}
                                        disabled={processing}
                                        placeholder={t('employees.create.placeholders.first_name', 'First name')}
                                    />
                                </div>
                                <InputError message={errors.first_name} />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="last_name">
                                    {t('employees.create.labels.last_name', 'Last name')}
                                    {requiredMark}
                                </Label>
                                <div className="relative">
                                    <User className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                                    <Input
                                        id="last_name"
                                        className="pl-10"
                                        required
                                        autoComplete="family-name"
                                        value={data.last_name}
                                        onChange={(e) => setData('last_name', e.target.value)}
                                        disabled={processing}
                                        placeholder={t('employees.create.placeholders.last_name', 'Last name')}
                                    />
                                </div>
                                <InputError message={errors.last_name} />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>
                                {t('employees.create.labels.email', 'Email address')}
                                {requiredMark}
                            </Label>
                            <div className="relative">
                                <Mail className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 z-10 h-4 w-4 -translate-y-1/2" />
                                <div className="[&>div>input]:pl-10">
                                    <EmailDomainInput
                                        label=""
                                        value={data.email}
                                        onChange={(value) => setData('email', value)}
                                        approvedDomains={approvedDomains}
                                        error={errors.email}
                                        required
                                        disabled={processing}
                                        placeholder={t('employees.create.placeholders.email', 'email')}
                                        autoComplete="email"
                                    />
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="rounded-md">
                    <CardHeader className="pb-4">
                        <div className="flex items-start gap-3">
                            <span className="bg-primary/12 text-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                                2
                            </span>
                            <div className="space-y-1">
                                <CardTitle>{t('employees.create.sections.extra_info', 'Additional information')}</CardTitle>
                                <CardDescription>{t('employees.create.sections.extra_info_desc', 'Contact and security details.')}</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-5">
                        <div className="space-y-2">
                            <Label htmlFor="password">{t('employees.create.labels.password', 'Password')}</Label>
                            <div className="relative">
                                <Lock className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                                <Input
                                    id="password"
                                    type="password"
                                    className="pl-10"
                                    required
                                    autoComplete="new-password"
                                    value={data.password}
                                    onChange={(e) => setData('password', e.target.value)}
                                    disabled={processing}
                                    placeholder={t('employees.create.placeholders.password', 'Password')}
                                />
                            </div>
                            <InputError message={errors.password} />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password_confirmation">{t('employees.create.labels.password_confirmation', 'Confirm password')}</Label>
                            <div className="relative">
                                <Lock className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                                <Input
                                    id="password_confirmation"
                                    type="password"
                                    className="pl-10"
                                    required
                                    autoComplete="new-password"
                                    value={data.password_confirmation}
                                    onChange={(e) => setData('password_confirmation', e.target.value)}
                                    disabled={processing}
                                    placeholder={t('employees.create.placeholders.password_confirmation', 'Confirm password')}
                                />
                            </div>
                            <InputError message={errors.password_confirmation} />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="phone">{t('employees.create.labels.phone', 'Phone')}</Label>
                            <div className="relative">
                                <Phone className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                                <Input
                                    id="phone"
                                    type="tel"
                                    className="pl-10"
                                    autoComplete="tel"
                                    value={data.phone}
                                    onChange={(e) => setData('phone', e.target.value)}
                                    disabled={processing}
                                    placeholder={t('employees.create.placeholders.phone', 'Phone number')}
                                />
                            </div>
                            <InputError message={errors.phone} />
                        </div>
                    </CardContent>
                </Card>

                <Card className="rounded-md">
                    <CardHeader className="pb-4">
                        <div className="flex items-start gap-3">
                            <span className="bg-primary/12 text-primary inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                                3
                            </span>
                            <div className="space-y-1">
                                <CardTitle>{t('employees.create.sections.work_info', 'Work information')}</CardTitle>
                                <CardDescription>{t('employees.create.sections.work_info_desc', 'Employee role and access.')}</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-5">
                        <div className="space-y-2">
                            <Label htmlFor="role">
                                {t('employees.create.labels.role', 'Role')}
                                {requiredMark}
                            </Label>
                            <div className="relative">
                                <BriefcaseBusiness className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 z-10 h-4 w-4 -translate-y-1/2" />
                                <Select value={data.role} onValueChange={(value) => setData('role', value)} disabled={processing}>
                                    <SelectTrigger id="role" className="w-full pl-10">
                                        <SelectValue placeholder={t('employees.create.placeholders.role', 'Select a role')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Administrator">{t('employees.create.roles.administrator', 'Administrator')}</SelectItem>
                                        <SelectItem value="Engineer">{t('employees.create.roles.engineer', 'Engineer')}</SelectItem>
                                        <SelectItem value="Manufacturing">{t('employees.create.roles.manufacturing', 'Manufacturing')}</SelectItem>
                                        <SelectItem value="Sales Manager">{t('employees.create.roles.sales_manager', 'Sales Manager')}</SelectItem>
                                        <SelectItem value="Service Manager">
                                            {t('employees.create.roles.service_manager', 'Service Manager')}
                                        </SelectItem>
                                        <SelectItem value="Service Supervisor">
                                            {t('employees.create.roles.service_supervisor', 'Service Supervisor')}
                                        </SelectItem>
                                        <SelectItem value="Service Worker">{t('employees.create.roles.service_worker', 'Service Worker')}</SelectItem>
                                        <SelectItem value="Project Manager">
                                            {t('employees.create.roles.project_manager', 'Project Manager')}
                                        </SelectItem>
                                        <SelectItem value="Purchasing Manager">
                                            {t('employees.create.roles.purchasing_manager', 'Purchasing Manager')}
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <InputError message={errors.role} />
                        </div>
                    </CardContent>
                </Card>

                <div className="border-primary/25 bg-primary/5 flex items-start gap-3 rounded-md border p-4">
                    <Info className="text-primary mt-0.5 h-5 w-5 shrink-0" />
                    <div>
                        <p className="text-primary text-sm font-semibold">{t('common.information', 'Information')}</p>
                        <p className="text-muted-foreground text-sm">
                            {t('employees.create.info_text', 'The employee will receive an email with login information and a password setup link.')}
                        </p>
                    </div>
                </div>

                <div className="flex flex-col-reverse justify-between gap-3 sm:flex-row sm:items-center">
                    <Button asChild type="button" variant="outline" className="min-w-[148px]">
                        <Link href="/employees">{t('common.cancel', 'Cancel')}</Link>
                    </Button>
                    <Button type="submit" className="min-w-[220px]" disabled={processing}>
                        {processing && <LoaderCircle className="h-4 w-4 animate-spin" />}
                        {t('employees.create.actions.submit', 'Create Employee')}
                    </Button>
                </div>
            </form>
        </CreateLayout>
    );
}
